import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { breachLabel } from "./format";
import OwnerCell from "./OwnerCell";
import TableCard from "../../components/ui-components/TableCard";
import EmptyState from "../../components/ui-components/EmptyState";
import FlexTable from "../../components/ui-components/FlexTable";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";

// StatusBadge renders a colored pill for the portfolio budget status.
// over=red, at_risk=amber/yellow, on_track=green/teal, not_measurable=neutral.
// Exported so sibling cost-control tables (e.g. Ownership) render an identical status column.
export function StatusBadge({ status }) {
  const s = status || "not_measurable";
  let cls =
    "inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium ";
  let label;
  if (s === "over") {
    cls += "bg-danger-soft text-danger";
    label = "Over";
  } else if (s === "at_risk") {
    cls += "bg-warning-soft text-warning";
    label = "At risk";
  } else if (s === "on_track") {
    cls += "bg-success-soft text-success";
    label = "On track";
  } else {
    cls += "bg-surface-2 text-text-2";
    label = "Not measurable";
  }
  return <span className={cls}>{label}</span>;
}
StatusBadge.propTypes = { status: PropTypes.string };

// Rank order for sorting: over first, then at_risk, then not_measurable, then on_track.
const RANK = { over: 0, at_risk: 1, not_measurable: 2, on_track: 3 };

const FILTER_OPTIONS = [
  { value: "exceptions", label: "Exceptions" },
  { value: "all", label: "All" },
];

export default function BudgetExceptionsTable({ budgetRisk, period, asOf, onSelect }) {
  const [showAll, setShowAll] = useState(false);

  const budgets = budgetRisk?.budgets || [];

  const columns = useMemo(
    () => [
      {
        accessor: "status",
        Header: () => <span>Status</span>,
        Cell: ({ value }) => <StatusBadge status={value} />,
        width: 130,
        minWidth: 120,
        maxWidth: 150,
      },
      {
        accessor: "name",
        Header: () => <span>Budget</span>,
        Cell: ({ value }) => <span className="font-medium text-text truncate">{value}</span>,
        flex: 1,
        minWidth: 200,
      },
      {
        id: "owner",
        Header: () => <span>Owner</span>,
        Cell: ({ row }) => (
          <span className="text-text-2">
            <OwnerCell owners={row.original.owners} owner={row.original.owner} details={row.original.owner_details} />
          </span>
        ),
        width: 200,
        minWidth: 160,
        maxWidth: 260,
      },
      {
        accessor: "pct_of_total",
        Header: () => <span>% of total spend</span>,
        Cell: ({ row }) =>
          row.original.status === "not_measurable" ? (
            <span className="text-text-3">—</span>
          ) : (
            <span className="tabular-nums text-text-2">{`${Math.round((row.original.pct_of_total || 0) * 100)}%`}</span>
          ),
        width: 140,
        minWidth: 120,
        maxWidth: 170,
      },
      {
        accessor: "projected_pct",
        Header: () => <span>% of cap</span>,
        Cell: ({ row }) => {
          if (row.original.status === "not_measurable") return <span className="text-text-3">—</span>;
          const overPct = (row.original.projected_pct || 0) > 1;
          return (
            <span className={`tabular-nums font-medium ${overPct ? "text-danger" : "text-text-2"}`}>
              {`${Math.round((row.original.projected_pct || 0) * 100)}%`}
            </span>
          );
        },
        width: 110,
        minWidth: 100,
        maxWidth: 130,
      },
      {
        id: "breach",
        Header: () => <span>Forecast breach</span>,
        Cell: ({ row }) => (
          <span className="tabular-nums text-text-2">{breachLabel(row.original.days_to_breach, period, asOf)}</span>
        ),
        width: 150,
        minWidth: 130,
        maxWidth: 180,
      },
      {
        id: "review",
        align: "right",
        Header: () => null,
        Cell: ({ row }) => (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onSelect(row.original);
            }}
            className="text-sm font-semibold text-primary opacity-0 transition-opacity hover:text-primary-hover group-hover:opacity-100 focus-visible:opacity-100"
          >
            Review
          </button>
        ),
        width: 110,
        minWidth: 100,
        maxWidth: 130,
      },
    ],
    [onSelect, period, asOf]
  );

  if (budgetRisk?.note === "no_active_budgets") {
    return (
      <div className="rounded-card border border-card-bd bg-card">
        <EmptyState
          icon={icon({ name: "wallet", style: "light" })}
          title="No active budgets"
          message="Active budgets will appear here once they're set up."
        />
      </div>
    );
  }
  if (budgets.length === 0) {
    return null;
  }

  const counts = budgetRisk?.counts || {};
  const onTrack = counts.on_track || 0;

  const rows = (showAll
    ? budgets
    : budgets.filter((b) => b.status !== "on_track")
  )
    .slice()
    .sort((a, b) => {
      const ra = RANK[a.status] ?? 99;
      const rb = RANK[b.status] ?? 99;
      if (ra !== rb) return ra - rb;
      return (b.projected_pct || 0) - (a.projected_pct || 0);
    });

  // Contextual hint: when filtering to exceptions, reassure about what's hidden;
  // when showing everything, just state the count. (The full status rollup lives
  // in the "Budgets" KPI tile, so we don't repeat it here.)
  const exceptionsCount = budgets.filter((b) => b.status !== "on_track").length;
  const hint = !showAll
    ? `${exceptionsCount} ${
        exceptionsCount === 1 ? "budget needs" : "budgets need"
      } attention · ${onTrack} on track`
    : `Showing all ${budgets.length} budgets`;

  return (
    <TableCard
      title="Budgets"
      subtitle={hint}
      filterValue={showAll ? "all" : "exceptions"}
      onFilterChange={(v) => setShowAll(v === "all")}
      filterOptions={FILTER_OPTIONS}
      filterAriaLabel="Show exceptions or all budgets"
      controlsPlacement="first-row"
    >
      {rows.length === 0 ? (
        <EmptyState
          icon={icon({ name: "circle-check", style: "light" })}
          title="Nothing needs attention"
          message="All budgets are on track."
          action={{ label: "Show all budgets", onClick: () => setShowAll(true), variant: "secondary" }}
        />
      ) : (
        <FlexTable
          columns={columns}
          data={rows}
          onRowClick={(id, b) => onSelect(b)}
          rowAriaLabel={(b) => `Review budget ${b.name}`}
        />
      )}
    </TableCard>
  );
}

BudgetExceptionsTable.propTypes = {
  budgetRisk: PropTypes.object,
  period: PropTypes.string,
  asOf: PropTypes.string,
  onSelect: PropTypes.func,
};
